import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './SpeechToTextInput.css'; // Custom CSS for styling
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import FontAwesomeIcon
import { faMicrophone, faStopCircle } from '@fortawesome/free-solid-svg-icons'; // Import specific icons

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

const SpeechToTextFormInput = ({
  label,
  value = '', // Default value as empty
  onChange = () => {}, // Default onChange function (noop)
  placeholder,
  type,
  onSubmit = () => {}, // Function to handle form submission
}) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [transcript, setTranscript] = useState(value); // Initialize with value prop
  const [interimText, setInterimText] = useState(''); // Track interim results
  const [micStatus, setMicStatus] = useState('Ready to listen.'); // Track microphone status
  const transcriptRef = useRef(transcript); // Use ref to preserve existing text

  // Memoize stopListening to avoid unnecessary re-renders
  const stopListening = useCallback(() => {
    if (recognition && isListening) {
      recognition.stop(); // Stop recognition if it's running
      setIsListening(false); // Update state
      setMicStatus('Microphone stopped. Click to resume.');
    }
  }, [recognition, isListening]);

  // Memoize the commands object with only clear and submit
  const commands = useMemo(() => ({
    clear: () => setTranscript(''), // Clears the input field
    submit: () => onSubmit(), // Trigger form submission
  }), [onSubmit]);

  // Memoize checkCommand to avoid unnecessary re-renders
  const checkCommand = useCallback((spokenText) => {
    const command = spokenText.toLowerCase().trim();
    if (commands[command]) {
      commands[command](); // Trigger the corresponding action
    }
  }, [commands]);

  // Keep transcriptRef updated with the latest transcript value
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Initialize SpeechRecognition once on mount
  useEffect(() => {
    if (SpeechRecognition && !recognition) {
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true; // Continuous mode
      recognitionInstance.interimResults = true; // Capture partial results
      recognitionInstance.lang = 'en-US'; // Set language

      recognitionInstance.onresult = (event) => {
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            final += event.results[i][0].transcript; // Final recognized speech
          } else {
            interim += event.results[i][0].transcript; // Interim speech
          }
        }

        // Display interim speech immediately
        if (interim) {
          setInterimText(interim); // Set the interim result while speaking
        }

        // Append final speech to transcript when it's completed
        if (final) {
          const updatedTranscript = transcriptRef.current + ' ' + final;
          setTranscript(updatedTranscript); // Update transcript
          transcriptRef.current = updatedTranscript; // Update ref with the latest transcript
          setInterimText(''); // Clear interim text once final result is processed

          // Call onChange to propagate updated transcript back to parent
          if (typeof onChange === 'function') {
            onChange(updatedTranscript);
          }

          // Check if the final transcript matches a command
          checkCommand(final);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        stopListening(); // Stop listening in case of an error
      };

      // Handle the microphone stopping due to inactivity or other reasons
      recognitionInstance.onend = () => {
        setIsListening(false); // Ensure isListening is updated
        setMicStatus('Microphone stopped. Click to resume.'); // Update status
      };

      setRecognition(recognitionInstance); // Only set once
    }
  }, [recognition, stopListening, onChange, checkCommand]); // Dependencies

  // Start the speech recognition
  const startListening = () => {
    if (recognition && !isListening) { // Only start if not already listening
      recognition.start();
      setIsListening(true);
      setInterimText(''); // Clear any interim text before starting
      setMicStatus('Listening...'); // Update mic status
    }
  };

  return (
    <div className="speech-to-text-wrapper">
      <label>{label}</label>
      <div className="input-container">
        {type === 'textarea' ? (
          <textarea
            value={transcript + interimText} // Display both final and interim text
            onChange={(e) => {
              const newValue = e.target.value;
              setTranscript(newValue); // Update local state
              transcriptRef.current = newValue; // Update ref when manually typing
              onChange(newValue); // Propagate change to parent
            }}
            placeholder={placeholder}
            className="speech-textarea"
          />
        ) : (
          <input
            type="text"
            value={transcript + interimText} // Display both final and interim text
            onChange={(e) => {
              const newValue = e.target.value;
              setTranscript(newValue); // Update local state
              transcriptRef.current = newValue; // Update ref when manually typing
              onChange(newValue); // Propagate change to parent
            }}
            placeholder={placeholder}
            className="speech-input"
          />
        )}

        <button
          type="button"
          onClick={startListening}
          className={`mic-button ${isListening ? 'listening' : ''}`}
          title="Start Listening"
        >
          <FontAwesomeIcon icon={faMicrophone} /> {/* Modern Microphone Icon */}
        </button>
        <button
          type="button"
          onClick={stopListening}
          className="mic-button stop-button"
          title="Stop Listening"
        >
          <FontAwesomeIcon icon={faStopCircle} /> {/* Modern Stop Icon */}
        </button>
      </div>

      {/* Microphone Status Message beneath the input field */}
      <div className="mic-status-container">
        <span className="mic-status">{micStatus}</span> 
      </div>
    </div>
  );
};

export default SpeechToTextFormInput;
